import React, { useState, useEffect, useRef } from "react";
import { useGlobalContext } from "../../context/stateContext";
import { useAuth } from "../../context/Auth";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import {
  Truck,
  Search,
  Plus,
  Trash2,
  Package,
  Calendar,
  ChevronDown,
  ChevronUp,
  Send,
  AlertCircle,
  Building2,
  RefreshCw,
} from "lucide-react";

const CreateInterbranch = () => {
  const searchRef = useRef();
  const { requestWithAuth } = useAuth();
  const { activeSession } = useGlobalContext();

  const [drugsDropdown, setDrugsDropdown] = useState(false);
  const [drugs, setDrugs] = useState([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedResultIndex, setSelectedResultIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const listContainerRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [selectedDrug, setSelectedDrug] = useState(null);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [insertedDrugs, setInsertedDrugs] = useState([]);
  const [recordingSale, setRecordingSale] = useState(false);
  const [totalCost, setTotalCost] = useState(0.0);
  const [quantityRequested, setQuantityRequested] = useState("");
  const [branches, setBranches] = useState([]);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [batchModalIndex, setBatchModalIndex] = useState(null);

  // Helper function to get earliest expiring batch with stock
  const getEarliestBatch = (batches) => {
    const availableBatches = batches.filter(
      (b) => parseFloat(b.quantity) > 0 && !isBatchExpired(b.expiry_date)
    );
    if (availableBatches.length === 0) return null;

    return availableBatches.reduce((earliest, current) => {
      return new Date(current.expiry_date) < new Date(earliest.expiry_date)
        ? current
        : earliest;
    });
  };

  const isBatchExpired = (expiryDate) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expiry = new Date(expiryDate);
    expiry.setHours(0, 0, 0, 0);
    return expiry < today;
  };

  // Helper function to check if expiry is soon (within 3 months)
  const isExpiringSoon = (expiryDate) => {
    const threeMonthsFromNow = new Date();
    threeMonthsFromNow.setMonth(threeMonthsFromNow.getMonth() + 3);
    return new Date(expiryDate) <= threeMonthsFromNow;
  };

  const fetchBranches = async () => {
    try {
      const data = await requestWithAuth(
        `/company/branches/?paginated=false`,
        "GET"
      );
      setBranches(data);
    } catch (error) {
      console.error("Error fetching branches:", error);
    }
  };

  const selectDrug = (drug) => {
    const totalStock = drug.batches.reduce(
      (sum, batch) => sum + parseFloat(batch.quantity),
      0
    );

    if (totalStock <= 0) {
      Swal.fire("", "This drug is out of stock", "error");
      return false;
    }

    // Auto-select earliest expiring batch
    const earliestBatch = getEarliestBatch(drug.batches);

    setSelectedDrug(drug);
    setSelectedBatch(earliestBatch);
    setDrugsDropdown(false);
    setSearch("");
    setDrugs([]);
  };

  const getDrugs = async (search_query) => {
    setLoading(true);
    try {
      const data = await requestWithAuth(
        `/inventory/with-batches/${activeSession?.branch?.id}?paginated=false&search=${search_query}`,
        "GET"
      );
      setDrugs(data);
    } catch (error) {
      console.error("Error fetching drugs:", error);
    } finally {
      setLoading(false);
      setSelectedResultIndex(-1);
    }
  };

  const recordTransfer = async () => {
    if (insertedDrugs.length <= 0) {
      return toast.error("Insert at least one drug");
    }

    Swal.fire({
      icon: "question",
      title: "You are about to make an interbranch transfer",
      text: "Would you like to proceed with it?",
      showDenyButton: true,
      confirmButtonText: "Yes Proceed!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setRecordingSale(true);
          const body = JSON.stringify({
            branch: activeSession?.branch?.id,
            session: activeSession?.session,
            transfer_items: insertedDrugs,
          });



          await requestWithAuth(`/inventory/interbranch/`, "POST", body, {
            "Content-Type": "application/json",
          });

          Swal.fire({
            icon: "success",
            title: "Transferred!",
            text: "Interbranch transfer initiated successfully!",
          }).then(() => {
            window.location.href = "/inventory/interbranch"
          });
        } catch (error) {
          if (error.response?.data?.error) {
            for (const property in error.response.data.error) {
              toast.error(
                `${property} error: ${error.response.data.error[property]}`
              );
            }
          }
          if (error.response?.status === 500) {
            toast.error("Something went wrong!");
          }
        } finally {
          setRecordingSale(false);
        }
      }
    });
  };

  const insertDrug = () => {
    if (!selectedDrug?.drug) {
      return toast.error("Select a drug first");
    }
    if (!selectedBatch) {
      return toast.error("No batch available");
    }
    if (!quantityRequested || quantityRequested <= 0) {
      return toast.error("Please enter a valid quantity");
    }

    const drugId = selectedDrug?.drug?.id;
    let remainingQty = parseFloat(quantityRequested);

    // Get total available stock across all batches
    const totalAvailableStock = selectedDrug.batches.reduce(
      (sum, batch) => sum + parseFloat(batch.quantity),
      0
    );

    if (remainingQty > totalAvailableStock) {
      return toast.error(
        `Only ${totalAvailableStock} units available across all batches`
      );
    }

    // Get available batches sorted by expiry date (earliest first)
    const availableBatches = selectedDrug.batches
      .filter((b) => parseFloat(b.quantity) > 0)
      .sort((a, b) => new Date(a.expiry_date) - new Date(b.expiry_date));

    const newInsertedDrugs = [...insertedDrugs];
    let batchesUsed = [];

    // Distribute quantity across batches starting with earliest expiry
    for (const batch of availableBatches) {
      if (remainingQty <= 0) break;

      // Check if this batch is already in cart
      const existingItemIndex = newInsertedDrugs.findIndex(
        (item) =>
          item.drug === drugId && item.batch_number === batch.batch_number
      );

      const batchAvailableQty = parseFloat(batch.quantity);
      const qtyToAdd = Math.min(remainingQty, batchAvailableQty);

      if (existingItemIndex >= 0) {
        // Update existing item in cart
        const currentQty = parseFloat(
          newInsertedDrugs[existingItemIndex].quantity
        );
        const newQty = currentQty + qtyToAdd;

        if (newQty > batchAvailableQty) {
          const canAdd = batchAvailableQty - currentQty;
          if (canAdd > 0) {
            newInsertedDrugs[existingItemIndex].quantity =
              batchAvailableQty.toString();
            const unitPrice = parseFloat(
              newInsertedDrugs[existingItemIndex].unit_price
            );
            newInsertedDrugs[existingItemIndex].total_cost = (
              unitPrice * batchAvailableQty
            ).toFixed(2);
            remainingQty -= canAdd;
            batchesUsed.push({ batch: batch.batch_number, qty: canAdd });
          }
        } else {
          newInsertedDrugs[existingItemIndex].quantity = newQty.toString();
          const unitPrice = parseFloat(
            newInsertedDrugs[existingItemIndex].unit_price
          );
          newInsertedDrugs[existingItemIndex].total_cost = (
            unitPrice * newQty
          ).toFixed(2);
          remainingQty -= qtyToAdd;
          batchesUsed.push({ batch: batch.batch_number, qty: qtyToAdd });
        }
      } else {
        // Add new item to cart
        const total_cost =
          parseFloat(selectedDrug?.drug?.selling_price) * qtyToAdd;
        newInsertedDrugs.push({
          drug: selectedDrug?.drug?.id,
          drug_name: selectedDrug?.drug?.name,
          batch_number: batch.batch_number,
          expiry_date: batch.expiry_date,
          available_quantity: batch.quantity,
          quantity: qtyToAdd.toString(),
          unit_price: selectedDrug?.drug?.selling_price,
          total_cost: total_cost.toFixed(2),
          has_multiple_batches:
            selectedDrug?.batches?.filter((b) => parseFloat(b.quantity) > 0)
              .length > 1,
        });
        remainingQty -= qtyToAdd;
        batchesUsed.push({ batch: batch.batch_number, qty: qtyToAdd });
      }
    }

    // Show notification about batch distribution
    if (batchesUsed.length > 1) {
      const batchInfo = batchesUsed
        .map((b) => `${b.qty} from ${b.batch}`)
        .join(", ");
      toast.success(`Split across ${batchesUsed.length} batches: ${batchInfo}`);
    }

    setInsertedDrugs(newInsertedDrugs);
    setSelectedDrug(null);
    setSelectedBatch(null);
    setQuantityRequested("");

    let total = 0;
    newInsertedDrugs.forEach((item) => {
      total += parseFloat(item?.total_cost);
    });
    setTotalCost(total.toFixed(2));
  };

  const removeInsertedDrug = (index) => {
    const newInsertedDrugs = [...insertedDrugs];
    newInsertedDrugs.splice(index, 1);
    setInsertedDrugs(newInsertedDrugs);

    let total = 0;
    newInsertedDrugs.forEach((item) => {
      total += parseFloat(item?.total_cost);
    });
    setTotalCost(total.toFixed(2));
  };

  const updateInsertedDrug = (index, new_quantity) => {
    const newInsertedDrugs = [...insertedDrugs];
    const availableQty =
      parseFloat(newInsertedDrugs[index]?.available_quantity) || 0;
    const unitPrice = parseFloat(newInsertedDrugs[index]?.unit_price) || 0;

    if (parseFloat(new_quantity) > availableQty) {
      return toast.error(
        "Quantity requested is greater than available stock in this batch"
      );
    }

    newInsertedDrugs[index].quantity = new_quantity;
    const parsedQty = parseFloat(new_quantity) || 0;
    newInsertedDrugs[index].total_cost = (unitPrice * parsedQty).toFixed(2);

    setInsertedDrugs(newInsertedDrugs);

    let total = 0;
    newInsertedDrugs.forEach((item) => {
      total += parseFloat(item?.total_cost) || 0;
    });

    setTotalCost(total.toFixed(2));
  };

  const openBatchModal = (index) => {
    setBatchModalIndex(index);
    setShowBatchModal(true);
  };

  const changeBatch = (newBatch) => {
    if (batchModalIndex !== null) {
      const newInsertedDrugs = [...insertedDrugs];
      const item = newInsertedDrugs[batchModalIndex];

      // Check if new batch already exists in cart
      const batchExists = insertedDrugs.some(
        (drug, idx) =>
          idx !== batchModalIndex &&
          drug.drug === item.drug &&
          drug.batch_number === newBatch.batch_number
      );

      if (batchExists) {
        toast.error("This batch is already in the cart");
        return;
      }

      // Update batch information
      newInsertedDrugs[batchModalIndex] = {
        ...item,
        batch_number: newBatch.batch_number,
        expiry_date: newBatch.expiry_date,
        available_quantity: newBatch.quantity,
        quantity: Math.min(
          parseFloat(item.quantity),
          parseFloat(newBatch.quantity)
        ).toString(),
      };

      // Recalculate cost
      const newQty = parseFloat(newInsertedDrugs[batchModalIndex].quantity);
      const unitPrice = parseFloat(
        newInsertedDrugs[batchModalIndex].unit_price
      );
      newInsertedDrugs[batchModalIndex].total_cost = (
        newQty * unitPrice
      ).toFixed(2);

      setInsertedDrugs(newInsertedDrugs);

      // Recalculate total
      let total = 0;
      newInsertedDrugs.forEach((item) => {
        total += parseFloat(item?.total_cost) || 0;
      });
      setTotalCost(total.toFixed(2));

      setShowBatchModal(false);
      setBatchModalIndex(null);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 1000);

    return () => {
      clearTimeout(handler);
    };
  }, [search]);

  useEffect(() => {
    if (debouncedSearch.length >= 3) {
      getDrugs(debouncedSearch);
    } else {
      setDrugs([]);
    }
  }, [debouncedSearch]);

  useEffect(() => {
    if (drugsDropdown) {
      setTimeout(() => {
        searchRef.current?.focus();
      }, 0);
    } else {
      setSelectedResultIndex(-1);
    }
  }, [drugsDropdown]);

  const handleSearchKeyDown = (e) => {
    if (!drugs.length) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedResultIndex((prev) =>
        prev < drugs.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedResultIndex((prev) => (prev > 0 ? prev - 1 : 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (selectedResultIndex >= 0 && selectedResultIndex < drugs.length) {
        selectDrug(drugs[selectedResultIndex]);
      }
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDrugsDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (selectedResultIndex >= 0 && listContainerRef.current) {
      const selectedElement = listContainerRef.current.querySelector(
        `#drug-result-${selectedResultIndex}`
      );
      if (selectedElement) {
        selectedElement.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }
    }
  }, [selectedResultIndex]);



  useEffect(() => {
    fetchBranches();
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Enter" && !drugsDropdown && !showBatchModal) {
        insertDrug();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [
    selectedDrug,
    selectedBatch,
    quantityRequested,
    drugsDropdown,
    showBatchModal,
  ]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.altKey && e.key.toLowerCase() === "t") {
        e.preventDefault();
        recordTransfer();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [insertedDrugs, totalCost]);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Batch Change Modal */}
      {showBatchModal && batchModalIndex !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  Select Batch for {insertedDrugs[batchModalIndex]?.drug_name}
                </h3>
                <button
                  onClick={() => {
                    setShowBatchModal(false);
                    setBatchModalIndex(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <RefreshCw size={24} />
                </button>
              </div>
            </div>

            <div className="p-6">
              {(() => {
                const currentDrug = insertedDrugs[batchModalIndex];
                // Find the drug with batches from drugs state or reconstruct
                const drugWithBatches = drugs.find(
                  (d) => d.drug?.id === currentDrug.drug
                );

                if (!drugWithBatches) {
                  return (
                    <p className="text-sm text-gray-500 text-center py-4">
                      No batches available
                    </p>
                  );
                }

                const availableBatches = drugWithBatches.batches
                  .filter((b) => parseFloat(b.quantity) > 0)
                  .sort(
                    (a, b) => new Date(a.expiry_date) - new Date(b.expiry_date)
                  );

                return (
                  <div className="space-y-2">
                    {availableBatches?.map((batch, idx) => (
                      <div
                        key={idx}
                        onClick={() => changeBatch(batch)}
                        className={`p-4 border rounded-lg cursor-pointer transition-colors ${currentDrug.batch_number === batch.batch_number
                          ? "border-blue-500 bg-blue-50"
                          : "border-gray-200 hover:border-blue-300 hover:bg-gray-50"
                          }`}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="font-semibold text-gray-900">
                                Batch: {batch.batch_number}
                              </span>
                              {isExpiringSoon(batch.expiry_date) && (
                                <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded">
                                  Expiring Soon
                                </span>
                              )}
                              {currentDrug.batch_number ===
                                batch.batch_number && (
                                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                                    Current
                                  </span>
                                )}
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                              <div>
                                <span className="font-medium">Available:</span>{" "}
                                {batch.quantity} units
                              </div>
                              <div>
                                <span className="font-medium">Expiry:</span>{" "}
                                {new Date(
                                  batch.expiry_date
                                ).toLocaleDateString()}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                <span>Inventory</span>
                <span>/</span>
                <span className="text-gray-900 font-medium">
                  Interbranch Transfer
                </span>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <Truck size={32} />
                Interbranch Transfer
              </h1>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2">
              <p className="text-sm text-gray-600">Source Branch</p>
              <p className="text-lg font-semibold text-gray-900">
                {activeSession?.branch?.name}
              </p>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left Panel - Transfer Entry */}
          <div className="lg:col-span-2 space-y-6">
            {/* Product Selection Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-50 rounded-lg">
                    <Package className="text-blue-500" size={24} />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">
                      Transfer Details
                    </h2>
                    <p className="text-sm text-gray-500">
                      Select products to transfer to warehouse
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-6 space-y-4">
                {/* Drug Search */}
                <div className="relative" ref={dropdownRef}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Search Product
                  </label>
                  <div
                    className="relative cursor-pointer border border-gray-300 rounded-lg px-4 py-2.5 text-sm flex justify-between items-center"

                    onClick={() => {
                      setDrugsDropdown(!drugsDropdown);
                    }}
                  >
                    <span
                      className={
                        selectedDrug?.drug?.name
                          ? "text-gray-900"
                          : "text-gray-400"
                      }
                    >
                      {selectedDrug?.drug?.name || "Select a product..."}
                    </span>
                    {drugsDropdown ? (
                      <ChevronUp size={20} className="text-gray-400" />
                    ) : (
                      <ChevronDown size={20} className="text-gray-400" />
                    )}
                  </div>

                  {drugsDropdown && (
                    <div
                      onClick={(e) => e.stopPropagation()}
                      className="absolute z-10 w-full mt-2 bg-white border border-gray-300 rounded-lg shadow-lg"
                    >
                      <div className="p-3 border-b border-gray-200">
                        <div className="relative">
                          <Search
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                            size={18}
                          />
                          <input
                            ref={searchRef}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            type="text"
                            onKeyDown={handleSearchKeyDown}
                            placeholder="Type to search..."
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                      <div className="max-h-64 overflow-y-auto" ref={listContainerRef}>
                        {search?.length < 3 ? (
                          <p className="p-4 text-sm text-gray-500 text-center">
                            Please enter at least 3 characters
                          </p>
                        ) : loading ? (
                          <p className="p-4 text-sm text-gray-500 text-center">
                            Searching...
                          </p>
                        ) : drugs?.length > 0 ? (
                          drugs?.map((drug_item, idx) => {
                            const totalStock = drug_item.batches.reduce(
                              (sum, batch) => sum + parseFloat(batch.quantity),
                              0
                            );
                            return (
                              <div
                                key={idx}
                                id={`drug-result-${idx}`}
                                onClick={() => selectDrug(drug_item)}
                                className={`p-3 cursor-pointer border-b border-gray-100 last:border-0 ${selectedResultIndex === idx
                                  ? "bg-blue-300"
                                  : "hover:bg-blue-50"
                                  }`}
                              >
                                <div className="flex justify-between items-center">
                                  <span className="text-sm font-medium text-gray-900">
                                    {drug_item?.drug?.name}
                                  </span>
                                  <span className="text-sm font-semibold text-blue-600">
                                    GHS {drug_item?.drug?.selling_price}
                                  </span>
                                </div>
                                <p className="text-xs text-gray-500 mt-1">
                                  Total Stock: {totalStock} |{" "}
                                  {drug_item.batches.length} batch(es)
                                </p>
                              </div>
                            );
                          })
                        ) : (
                          <p className="p-4 text-sm text-gray-500 text-center">
                            No results found
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Product & Batch Details Grid */}
                {selectedDrug?.drug && selectedBatch && (
                  <div className="space-y-3">
                    {/* Batch Warning if expiring soon */}
                    {isExpiringSoon(selectedBatch.expiry_date) && (
                      <div className="flex items-start gap-2 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                        <AlertCircle
                          className="text-orange-500 flex-shrink-0 mt-0.5"
                          size={18}
                        />
                        <div className="text-sm">
                          <p className="font-medium text-orange-900">
                            Expiring Soon
                          </p>
                          <p className="text-orange-700">
                            This batch expires on{" "}
                            {new Date(
                              selectedBatch.expiry_date
                            ).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Primary Batch
                        </label>
                        <div className="text-sm font-semibold text-gray-900">
                          {selectedBatch?.batch_number}
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Available Stock
                        </label>
                        <div className="text-sm font-semibold text-gray-900">
                          {selectedBatch?.quantity} units
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Unit Price
                        </label>
                        <div className="text-sm font-semibold text-gray-900">
                          GHS {selectedDrug?.drug?.selling_price}
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          <Calendar size={12} className="inline mr-1" />
                          Expiry Date
                        </label>
                        <div className="text-sm font-semibold text-gray-900">
                          {new Date(
                            selectedBatch?.expiry_date
                          ).toLocaleDateString()}
                        </div>
                      </div>
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Quantity
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={quantityRequested}
                          onChange={(e) => setQuantityRequested(e.target.value)}
                          placeholder="0"
                          className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      {/* Batch Distribution Preview */}
                      {quantityRequested &&
                        parseFloat(quantityRequested) > 0 && (
                          <div className="col-span-2">
                            {(() => {
                              const requestedQty =
                                parseFloat(quantityRequested);
                              const availableBatches = selectedDrug.batches
                                .filter((b) => parseFloat(b.quantity) > 0)
                                .sort(
                                  (a, b) =>
                                    new Date(a.expiry_date) -
                                    new Date(b.expiry_date)
                                );

                              const totalAvailable = availableBatches.reduce(
                                (sum, b) => sum + parseFloat(b.quantity),
                                0
                              );

                              if (requestedQty > totalAvailable) {
                                return (
                                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                                    <div className="flex items-start gap-2">
                                      <AlertCircle
                                        className="text-red-500 flex-shrink-0 mt-0.5"
                                        size={16}
                                      />
                                      <div className="text-xs text-red-700">
                                        <p className="font-medium">
                                          Insufficient Stock
                                        </p>
                                        <p>
                                          Only {totalAvailable} units available
                                          across all batches
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                );
                              }

                              // Calculate distribution
                              let remaining = requestedQty;
                              const distribution = [];

                              for (const batch of availableBatches) {
                                if (remaining <= 0) break;
                                const batchQty = parseFloat(batch.quantity);
                                const qtyFromBatch = Math.min(
                                  remaining,
                                  batchQty
                                );
                                distribution.push({
                                  batch: batch.batch_number,
                                  qty: qtyFromBatch,
                                  expiry: batch.expiry_date,
                                });
                                remaining -= qtyFromBatch;
                              }

                              if (distribution.length > 1) {
                                return (
                                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                    <div className="flex items-start gap-2">
                                      <Package
                                        className="text-blue-500 flex-shrink-0 mt-0.5"
                                        size={16}
                                      />
                                      <div className="text-xs">
                                        <p className="font-medium text-blue-900 mb-1">
                                          Will use {distribution.length}{" "}
                                          batches:
                                        </p>
                                        {distribution?.map((d, idx) => (
                                          <div
                                            key={idx}
                                            className="text-blue-700"
                                          >
                                            • {d.qty} from {d.batch} (exp:{" "}
                                            {new Date(
                                              d.expiry
                                            ).toLocaleDateString()}
                                            )
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  </div>
                                );
                              }

                              return null;
                            })()}
                          </div>
                        )}
                    </div>
                  </div>
                )}

                {/* Add Button */}
                <button
                  onClick={insertDrug}
                  disabled={
                    !selectedDrug?.drug || !quantityRequested || !selectedBatch
                  }
                  className="w-full bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-6 py-3 rounded-lg font-medium transition-colors shadow-sm flex items-center justify-center gap-2"
                >
                  <Plus size={20} />
                  Add to Transfer List
                </button>
              </div>
            </div>
          </div>

          {/* Right Panel - Transfer List */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 sticky top-6">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Package size={20} />
                  Transfer List ({insertedDrugs.length})
                </h2>
              </div>

              <div className="max-h-[calc(100vh-300px)] overflow-y-auto">
                {insertedDrugs.length > 0 ? (
                  <div className="divide-y divide-gray-200">
                    {insertedDrugs?.map((drug, index) => (
                      <div key={index} className="p-4 hover:bg-gray-50">
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex-1">
                            <h3 className="text-sm font-medium text-gray-900">
                              {drug?.drug_name}
                            </h3>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-xs text-gray-500">
                                Batch: {drug?.batch_number}
                              </span>
                              {isExpiringSoon(drug?.expiry_date) && (
                                <span className="text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded">
                                  Expiring Soon
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-500 mt-0.5">
                              Exp:{" "}
                              {new Date(drug?.expiry_date).toLocaleDateString()}
                            </p>
                          </div>
                          <button
                            onClick={() => removeInsertedDrug(index)}
                            className="text-red-500 hover:text-red-700 p-1"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>

                        <div className="flex items-center justify-between gap-2 mt-3">
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              min="1"
                              value={drug?.quantity}
                              onChange={(e) =>
                                updateInsertedDrug(index, e.target.value)
                              }
                              className="w-16 border border-gray-300 rounded px-2 py-1 text-sm text-center"
                            />
                            <span className="text-xs text-gray-500">
                              × GHS {drug?.unit_price}
                            </span>
                          </div>
                          <span className="text-sm font-semibold text-gray-900">
                            GHS {drug?.total_cost}
                          </span>
                        </div>

                        {drug?.has_multiple_batches && (
                          <button
                            onClick={() => openBatchModal(index)}
                            className="mt-3 w-full text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center justify-center gap-1 py-1.5 border border-blue-200 rounded hover:bg-blue-50 transition-colors"
                          >
                            <RefreshCw size={12} />
                            Change Batch
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-8 text-center">
                    <Package size={48} className="mx-auto text-gray-300 mb-3" />
                    <p className="text-sm text-gray-500">
                      Transfer list is empty
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      Add products to get started
                    </p>
                  </div>
                )}
              </div>

              {insertedDrugs.length > 0 && (
                <div className="border-t border-gray-200">
                  <div className="p-6 bg-gray-50">
                    <div className="flex justify-between items-center mb-4">
                      <span className="text-lg font-semibold text-gray-900">
                        Total Value
                      </span>
                      <span className="text-2xl font-bold text-blue-600">
                        GHS {totalCost}
                      </span>
                    </div>

                    {/* Transfer Button */}
                    <button
                      onClick={recordTransfer}
                      disabled={recordingSale}
                      className="w-full bg-green-500 hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-6 py-3 rounded-lg font-medium transition-colors shadow-sm flex items-center justify-center gap-2"
                    >
                      {recordingSale ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                          Processing Transfer...
                        </>
                      ) : (
                        <>
                          <Send size={20} />
                          Initiate Transfer (Alt+T)
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateInterbranch;
